import Editor from "@monaco-editor/react";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { memo, useEffect, useRef, useState } from "react";
import alqemiDataService, { Candidate, CV } from "../../api/AlqemiDataService";
import CircularLoader from "../../common/components/fundamentals/Loader/CircularLoader";
import { usePopUp } from "../../common/context/PopUpProvider";
import { useSnackbar } from "../../common/context/SnackbarProvider";
import { useUser } from "../../common/context/UserProvider";
import { HeadingText, SmallText } from "../../common/styles/Text.styles";
import CVPDF from "../../cv-generation/CVPDF";
import { replaceWhitespaceForDash } from "../../utils";
import {
  SettingsArea,
  SettingsButtonSection,
} from "../../pages/Settings/Settings.styles";
import {
  borderStyle,
  defaultGap,
  perelynPrimary,
  smallerGap,
  smallGap,
  tinyGap,
} from "../../styles/consts";
import PopUpBox from "../components/PopUpBox";
import AddProject from "./AddProject";

// Memoized CVPDF wrapper to prevent re-renders
const MemoizedCVPDF = memo(
  ({ cvContent, setPdfDownloadBlob }: { cvContent: any; setPdfDownloadBlob?: (blob: Blob | null) => void }) => {
    return <CVPDF cvContent={cvContent} setPdfDownloadBlob={setPdfDownloadBlob || (() => {})} />;
  },
  (prevProps, nextProps) => {
    // Only re-render if cvContent actually changed
    return (
      JSON.stringify(prevProps.cvContent) ===
      JSON.stringify(nextProps.cvContent)
    );
  }
);

export default function EditBaseCV() {
  const { user } = useUser();
  const { showSnackbar } = useSnackbar();
  const { showPopUpWindow } = usePopUp();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newCvName, setNewCvName] = useState("");
  const [newCvContent, setNewCvContent] = useState({});
  const [newCvEditingContent, setNewCvEditingContent] = useState<any>(null);
  const [hasUnsavedNewCvChanges, setHasUnsavedNewCvChanges] = useState(false);
  const newCvEditorRef = useRef<any>(null);
  const [editingCvId, setEditingCvId] = useState<string | null>(null);
  const [editingCvName, setEditingCvName] = useState("");
  const [closeAddCvPopup, setCloseAddCvPopup] = useState(false);
  const [editingCvContent, setEditingCvContent] = useState<{
    [key: string]: any;
  }>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<{
    [key: string]: boolean;
  }>({});
  const editorRefs = useRef<{ [key: string]: any }>({});
  const handleSaveCvRef = useRef<(cvId: string) => void>(() => {});
  const [pdfDownloadBlobs, setPdfDownloadBlobs] = useState<{ [key: string]: Blob | null }>({});
  const [isSaving, setIsSaving] = useState<{ [key: string]: boolean }>({});
  const email = user.email;

  const handleDownloadCV = (cv: CV) => {
    const blob = pdfDownloadBlobs[cv.id];
    if (blob && candidate) {
      const aTag = document.createElement("a");
      aTag.href = URL.createObjectURL(blob);
      aTag.style.display = "none";
      aTag.download = `Perelyn_Profile_${replaceWhitespaceForDash(
        candidate.name || "Unknown",
      )}_${cv.name || "CV"}.pdf`;
      document.body.appendChild(aTag);
      aTag.click();
      document.body.removeChild(aTag);
    }
  };

  useEffect(() => {
    const fetchCandidateData = async () => {
      setIsLoading(true);
      try {
        const candidates = await alqemiDataService.getCandidates();

        const matchingCandidate = candidates.find(
          (candidate) => candidate.email?.toLowerCase() === email?.toLowerCase()
        );

        if (matchingCandidate) {
          setCandidate(matchingCandidate);
          // Pre-populate newCvContent with first CV's content for the Add CV popup
          if (matchingCandidate.cvs && matchingCandidate.cvs.length > 0) {
            setNewCvContent(matchingCandidate.cvs[0].content);
          }
        }
      } catch (error) {
        showSnackbar("Error loading profile data", true);
      }
      setIsLoading(false);
    };

    fetchCandidateData();
  }, []);

  const handleSaveCv = async (cvId: string) => {
    if (candidate && editingCvContent[cvId]) {
      setIsSaving((prev) => ({ ...prev, [cvId]: true }));
      try {
        await alqemiDataService.updateCV(
          candidate.consultant_id || candidate.id,
          cvId,
          editingCvContent[cvId]
        );

        const updatedCandidate = await alqemiDataService.getCandidate(
          candidate.consultant_id || candidate.id
        );
        setCandidate(updatedCandidate);

        const editor = editorRefs.current[cvId];
        if (editor) {
          const updatedCv = updatedCandidate.cvs?.find(
            (cv: CV) => cv.id === cvId
          );
          if (updatedCv) {
            editor.setValue(JSON.stringify(updatedCv.content, null, 2));
            setEditingCvContent((prev) => ({
              ...prev,
              [cvId]: updatedCv.content,
            }));
          }
        }

        setHasUnsavedChanges((prev) => ({ ...prev, [cvId]: false }));

        showSnackbar("CV saved successfully");
      } catch (error) {
        showSnackbar("Error saving CV", true);
      } finally {
        setIsSaving((prev) => ({ ...prev, [cvId]: false }));
      }
    }
  };

  handleSaveCvRef.current = handleSaveCv;

  const handleEditorChange = (cvId: string, value: string | undefined) => {
    try {
      const parsedData = JSON.parse(value || "{}");
      setEditingCvContent((prev) => ({ ...prev, [cvId]: parsedData }));
      setHasUnsavedChanges((prev) => ({ ...prev, [cvId]: true }));
    } catch (error) {
      // Invalid JSON, don't update
    }
  };

  const handlePopupClose = (cvId: string) => {
    setEditingCvContent((prev) => {
      const newContent = { ...prev };
      delete newContent[cvId];
      return newContent;
    });
    setHasUnsavedChanges((prev) => ({ ...prev, [cvId]: false }));

    // Clear the editor reference to ensure fresh state on next open
    delete editorRefs.current[cvId];
  };

  const handleNewCvEditorChange = (value: string | undefined) => {
    try {
      const parsedData = JSON.parse(value || "{}");
      setNewCvEditingContent(parsedData);
      setHasUnsavedNewCvChanges(true);
    } catch (error) {
      // Invalid JSON, don't update
    }
  };

  const handleSaveNewCvChanges = () => {
    // Save changes to preview only (not to backend)
    if (newCvEditingContent) {
      setNewCvContent(newCvEditingContent);
      setHasUnsavedNewCvChanges(false);

      if (newCvEditorRef.current) {
        newCvEditorRef.current.setValue(
          JSON.stringify(newCvEditingContent, null, 2)
        );
      }
    }
  };

  const handleAddNewCv = async () => {
    if (candidate && newCvName.trim()) {
      try {
        // Use the saved content (newCvContent) which includes any saved changes
        await alqemiDataService.addCV(
          candidate.consultant_id || candidate.id,
          newCvName,
          newCvContent || {}
        );

        const updatedCandidate = await alqemiDataService.getCandidate(
          candidate.consultant_id || candidate.id
        );
        setCandidate(updatedCandidate);

        setNewCvName("");
        if (updatedCandidate.cvs && updatedCandidate.cvs.length > 0) {
          setNewCvContent(updatedCandidate.cvs[0].content);
        }

        showSnackbar("New CV added successfully");

        setCloseAddCvPopup(true);
      } catch (error: any) {
        showSnackbar(error.message || "Error adding new CV", true);
      }
    } else {
      showSnackbar("Please enter a CV name", true);
    }
  };

  const handleNewCvPopupClose = () => {
    setNewCvEditingContent(null);
    setHasUnsavedNewCvChanges(false);
    setNewCvName("");

    newCvEditorRef.current = null;
  };

  const handleDeleteCv = (cvId: string) => {
    if (candidate) {
      const deleteAction = async () => {
        try {
          await alqemiDataService.deleteCV(
            candidate.consultant_id || candidate.id,
            cvId
          );

          const updatedCandidate = await alqemiDataService.getCandidate(
            candidate.consultant_id || candidate.id
          );
          setCandidate(updatedCandidate);

          showSnackbar("CV deleted successfully");
        } catch (error) {
          showSnackbar("Error deleting CV", true);
        }
      };

      showPopUpWindow(
        deleteAction,
        "Delete CV",
        "Are you sure you want to delete this CV? This action cannot be undone.",
        "Cancel",
        "Delete"
      );
    }
  };

  const handleUpdateCvName = async (cvId: string) => {
    if (candidate && editingCvName.trim()) {
      try {
        await alqemiDataService.updateCVName(
          candidate.consultant_id || candidate.id,
          cvId,
          editingCvName
        );

        const updatedCandidate = await alqemiDataService.getCandidate(
          candidate.consultant_id || candidate.id
        );
        setCandidate(updatedCandidate);

        setEditingCvId(null);
        setEditingCvName("");

        showSnackbar("CV name updated successfully");
      } catch (error) {
        showSnackbar("Error updating CV name", true);
      }
    }
  };

  const startEditingName = (cvId: string, currentName: string) => {
    setEditingCvId(cvId);
    setEditingCvName(currentName);
  };

  const cancelEditingName = () => {
    setEditingCvId(null);
    setEditingCvName("");
  };

  const handleAddProject = async (project: any) => {
    if (candidate) {
      try {
        await alqemiDataService.addProjectToAllCVs(
          candidate.consultant_id || candidate.id,
          project
        );

        const updatedCandidate = await alqemiDataService.getCandidate(
          candidate.consultant_id || candidate.id
        );
        setCandidate(updatedCandidate);

        showSnackbar("Project added to all CVs successfully");
      } catch (error: any) {
        showSnackbar(error.message || "Error saving project", true);
      }
    }
  };

  const cvs = candidate?.cvs || [];

  return (
    <SettingsArea>
      <Stack>
        <Stack sx={{ borderBottom: borderStyle }}>
          <Stack sx={{ padding: smallerGap }}>
            <HeadingText>Manage Your CVs</HeadingText>
            <SmallText>
              You can have up to 10 different CV templates. Each CV can be
              tailored for different types of roles.
            </SmallText>
          </Stack>
        </Stack>

        <Stack sx={{ padding: `${smallGap} ${defaultGap}`, gap: defaultGap }}>
          {isLoading ? (
            <Stack
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "2rem",
              }}
            >
              <CircularLoader size={"2rem"} />
            </Stack>
          ) : (
            <Stack sx={{ gap: smallGap }}>
              <SmallText>
                {!candidate
                  ? "Your profile was not found. If you are an alqemi consultant - please contact support."
                  : `Please modify only the content - not the JSON structure. You have ${cvs.length}/10 CVs.`}
              </SmallText>

              <Stack
                sx={{
                  gap: "0.75rem",
                  maxHeight: "185px",
                  overflow: "auto",
                  paddingRight: "0.5rem",
                }}
              >
                {cvs.map((cv: CV) => (
                  <Paper
                    key={cv.id}
                    elevation={0}
                    sx={{
                      padding: `${tinyGap} ${smallGap} ${tinyGap}`,
                      border: borderStyle,
                      borderRadius: 0,
                      height: "100%",
                      backgroundColor: "#fafafa",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <Stack sx={{ gap: "0.2rem" }}>
                      {/* Title at the top */}
                      {editingCvId === cv.id ? (
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{ gap: "0.5rem" }}
                        >
                          <TextField
                            value={editingCvName}
                            onChange={(e) => setEditingCvName(e.target.value)}
                            size="small"
                            sx={{
                              flex: 1,
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "white",
                                borderRadius: 0,
                              },
                            }}
                            placeholder="CV Name"
                            autoFocus
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateCvName(cv.id)}
                            color="primary"
                            title="Save"
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={cancelEditingName}
                            color="default"
                            title="Cancel"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{ gap: "0.5rem" }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              flex: 1,
                              fontWeight: 500,
                              fontSize: "1rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cv.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => startEditingName(cv.id, cv.name)}
                            sx={{ opacity: 0.7, "&:hover": { opacity: 1 } }}
                            title="Edit name"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}

                      {/* Row with last updated and action buttons */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#ff5950",
                            fontSize: "0.75rem",
                          }}
                        >
                          Last updated:{" "}
                          {new Date(cv.updated_at).toLocaleDateString()}
                        </Typography>

                        <Stack
                          direction="row"
                          sx={{ gap: "0.5rem", alignItems: "center" }}
                        >
                          <PopUpBox
                            button={
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                  "&&": {
                                    textTransform: "none",
                                    borderColor: "#d0d0d0",
                                    borderRadius: 0,
                                    color: "#242424",
                                    "&:hover": {
                                      borderColor: "#242424",
                                      backgroundColor: "transparent",
                                    },
                                  },
                                }}
                              >
                                Edit CV
                              </Button>
                            }
                            heading={`Edit CV: ${cv.name}`}
                            width="80%"
                            height="90%"
                            onClose={() => handlePopupClose(cv.id)}
                          >
                            <Stack
                              sx={{
                                height: "100%",
                                width: "100%",
                                maxWidth: "100%",
                                overflow: "auto",
                                border: borderStyle,
                                display: "flex",
                                flexDirection: "row",
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: "50%",
                                  height: "100%",
                                  overflow: "auto",
                                  backgroundColor: "rgb(246, 246, 246)",
                                  position: "relative",
                                }}
                              >
                                <Editor
                                  height="100%"
                                  defaultLanguage="json"
                                  defaultValue={JSON.stringify(
                                    cv.content,
                                    null,
                                    2
                                  )}
                                  onChange={(value) =>
                                    handleEditorChange(cv.id, value)
                                  }
                                  onMount={(editor, monaco) => {
                                    editorRefs.current[cv.id] = editor;

                                    setEditingCvContent((prev) => ({
                                      ...prev,
                                      [cv.id]: cv.content,
                                    }));

                                    setHasUnsavedChanges((prev) => ({
                                      ...prev,
                                      [cv.id]: false,
                                    }));

                                    editor.addAction({
                                      id: "save-cv",
                                      label: "Save CV",
                                      keybindings: [
                                        monaco.KeyMod.CtrlCmd |
                                          monaco.KeyCode.KeyS,
                                      ],
                                      run: () => {
                                        handleSaveCvRef.current(cv.id);
                                      },
                                    });

                                    // Set the value after mount to ensure it's loaded
                                    editor.setValue(
                                      JSON.stringify(cv.content, null, 2)
                                    );
                                  }}
                                  theme="vs-light"
                                  options={{
                                    minimap: { enabled: false },
                                    fontSize: 14,
                                    wordWrap: "on",
                                    automaticLayout: true,
                                    scrollBeyondLastLine: false,
                                  }}
                                />
                                <Tooltip
                                  title={hasUnsavedChanges[cv.id] ? "Save changes first to download the latest version" : ""}
                                  arrow
                                >
                                  <span style={{ position: "absolute", bottom: 16, left: 16, zIndex: 10 }}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleDownloadCV(cv)}
                                      disabled={!pdfDownloadBlobs[cv.id] || hasUnsavedChanges[cv.id]}
                                      startIcon={<DownloadIcon />}
                                      sx={{
                                        backgroundColor: "#1976d2",
                                        "&:hover": {
                                          backgroundColor: "#1565c0",
                                        },
                                        boxShadow: 2,
                                        textTransform: "none",
                                      }}
                                    >
                                      Download
                                    </Button>
                                  </span>
                                </Tooltip>
                                {/* Save button overlay - only show when there are unsaved changes */}
                                {hasUnsavedChanges[cv.id] && (
                                  <>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleSaveCv(cv.id)}
                                      disabled={isSaving[cv.id]}
                                      startIcon={
                                        isSaving[cv.id] ? (
                                          <CircularProgress size={16} color="inherit" />
                                        ) : (
                                          <SaveIcon />
                                        )
                                      }
                                      sx={{
                                        position: "absolute",
                                        bottom: 16,
                                        right: 16,
                                        zIndex: 10,
                                        backgroundColor: "#ff5950",
                                        "&:hover": {
                                          backgroundColor: "#e54842",
                                        },
                                        boxShadow: 2,
                                        textTransform: "none",
                                      }}
                                    >
                                      {isSaving[cv.id] ? "Saving..." : "Save"}
                                    </Button>
                                    {/* Hint for keyboard shortcut */}
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        position: "absolute",
                                        bottom: 52,
                                        right: 16,
                                        zIndex: 10,
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.9)",
                                        padding: "4px 19px",
                                        color: "#666",
                                      }}
                                    >
                                      {navigator.platform.includes("Mac")
                                        ? "Cmd+S"
                                        : "Ctrl+S"}{" "}
                                    </Typography>
                                  </>
                                )}
                              </Box>
                              <MemoizedCVPDF
                                cvContent={cv.content}
                                setPdfDownloadBlob={(blob) =>
                                  setPdfDownloadBlobs((prev) => ({ ...prev, [cv.id]: blob }))
                                }
                              />
                            </Stack>
                          </PopUpBox>

                          <IconButton
                            onClick={() => handleDeleteCv(cv.id)}
                            size="small"
                            disabled={cvs.length === 1}
                            sx={{
                              color: "#ff5950",
                              opacity: cvs.length === 1 ? 0.3 : 0.7,
                              "&:hover": {
                                opacity: cvs.length === 1 ? 0.3 : 1,
                                backgroundColor: "rgba(255, 89, 80, 0.04)",
                              },
                              "&:disabled": {
                                color: "#ff5950",
                              },
                            }}
                            title={
                              cvs.length === 1
                                ? "Cannot delete last CV"
                                : "Delete CV"
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>

      <SettingsButtonSection>
        <AddProject
          onSave={handleAddProject}
          disabled={isLoading || !candidate}
        />
        <PopUpBox
          button={
            <Button
              variant="primary"
              disabled={isLoading || !candidate || cvs.length >= 10}
              sx={{
                "&&": {
                  border: "none",
                  borderLeft: borderStyle,
                  borderRadius: 0,
                  "&:disabled": {
                    border: "none",
                    borderLeft: borderStyle,
                  },
                },
              }}
            >
              + Add CV ({cvs.length}/10)
            </Button>
          }
          heading={"Add New CV Template"}
          width="85%"
          height="90%"
          closePopUp={closeAddCvPopup}
          setClosePopUp={setCloseAddCvPopup}
          onClose={handleNewCvPopupClose}
        >
          <Stack
            sx={{
              height: "100%",
              width: "100%",
              gap: "1rem",
              padding: "1rem",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
            }}
          >
            <Stack sx={{ gap: "0.5rem" }}>
              <Typography
                sx={{
                  color: perelynPrimary,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                CV Name
              </Typography>
              <TextField
                value={newCvName}
                onChange={(e) => setNewCvName(e.target.value)}
                placeholder="e.g., Frontend Developer CV, Data Scientist CV"
                fullWidth
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 0,
                    "&.Mui-focused fieldset": {
                      borderColor: "#ff5950",
                    },
                  },
                }}
              />
            </Stack>

            <Stack
              sx={{
                flex: 1,
                width: "100%",
                minHeight: 0,
                border: borderStyle,
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  overflow: "auto",
                  backgroundColor: "rgb(246, 246, 246)",
                  position: "relative",
                }}
              >
                <Editor
                  height="100%"
                  defaultLanguage="json"
                  defaultValue={JSON.stringify(newCvContent, null, 2)}
                  onChange={handleNewCvEditorChange}
                  onMount={(editor, monaco) => {
                    newCvEditorRef.current = editor;

                    setNewCvEditingContent(newCvContent);
                    setHasUnsavedNewCvChanges(false);

                    editor.addAction({
                      id: "save-new-cv-changes",
                      label: "Save Changes",
                      keybindings: [
                        monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                      ],
                      run: () => {
                        handleSaveNewCvChanges();
                      },
                    });

                    editor.setValue(JSON.stringify(newCvContent, null, 2));
                  }}
                  theme="vs-light"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    wordWrap: "on",
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                  }}
                />
                {/* Save button overlay - only show when there are unsaved changes */}
                {hasUnsavedNewCvChanges && (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveNewCvChanges}
                      startIcon={<SaveIcon />}
                      sx={{
                        position: "absolute",
                        bottom: 16,
                        right: 16,
                        zIndex: 10,
                        backgroundColor: "#ff5950",
                        "&:hover": {
                          backgroundColor: "#e54842",
                        },
                        boxShadow: 2,
                        textTransform: "none",
                      }}
                    >
                      Save
                    </Button>
                    {/* Hint for keyboard shortcut */}
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        bottom: 52,
                        right: 16,
                        zIndex: 10,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        padding: "4px 19px",
                        color: "#666",
                      }}
                    >
                      {navigator.platform.includes("Mac") ? "Cmd+S" : "Ctrl+S"}{" "}
                    </Typography>
                  </>
                )}
              </Box>
              <Stack sx={{ width: "50%", overflow: "auto" }}>
                <MemoizedCVPDF cvContent={newCvContent} />
              </Stack>
            </Stack>

            <Button
              variant="primary"
              onClick={handleAddNewCv}
              disabled={!newCvName.trim()}
              sx={{
                alignSelf: "flex-end",
              }}
            >
              Save New CV
            </Button>
          </Stack>
        </PopUpBox>
      </SettingsButtonSection>
    </SettingsArea>
  );
}
